import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import { getCurrentUser } from "../../services/auth_service";
import {
    isSuperadmin,
    hasPermission,
} from "../utils/permissions";
import {
    getSuppliers,
    getSupplierById,
    getSupplierTypes,
    getServiceLocations,
    getBranches,
    getStatuses,
    getFinancialYears,
    deleteSupplier,
} from "../../services/supplier_service";

import "./Suppliers.css";

import {
    FiSearch, FiPlus, FiEdit2, FiTrash2, FiX,
    FiFilter, FiAlertCircle, FiRefreshCw,
    FiUsers,
    FiChevronLeft, FiChevronRight,
    FiUser, FiFileText,
    FiCreditCard, FiDroplet, FiZap, FiEye, FiDownload,
    FiPrinter, FiChevronDown
} from "react-icons/fi";

const DOC_HOST = "https://srinivasafleet.com/api";
const resolveDocUrl = (url) => {
    if (!url) return url;
    if (url.startsWith(DOC_HOST)) return url;
    if (/^https?:\/\//i.test(url)) {
        try {
            const path = new URL(url).pathname;
            return `${DOC_HOST}${path}`;
        } catch {
            return url;
        }
    }
    return `${DOC_HOST}${url.startsWith("/") ? "" : "/"}${url}`;
};

const formatServerDateTime = (value) => {
    if (!value) return "-";
    const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
    const iso = hasOffset ? value : `${value}Z`;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", { timeZone: "Asia/Kolkata" });
};

const OUTSTANDING_RANGE_OPTIONS = [
    { value: "", label: "All Outstanding" },
    { value: "no_dues", label: "No Dues" },
    { value: "0", label: "₹0 – ₹49,999" },
    { value: "50000", label: "₹50,000 – ₹99,999" },
    { value: "100000", label: "₹1L – ₹1.99L" },
    { value: "200000", label: "₹2L – ₹2.99L" },
    { value: "300000", label: "₹3L – ₹3.99L" },
    { value: "400000", label: "₹4L – ₹4.99L" },
    { value: "500000", label: "₹5L – ₹5.99L" },
    { value: "600000", label: "₹6L – ₹6.99L" },
    { value: "700000", label: "₹7L – ₹7.99L" },
    { value: "800000", label: "₹8L – ₹8.99L" },
    { value: "900000", label: "₹9L – ₹9.99L" },
    { value: "1000000", label: "₹10L & above" },
];

const OUTSTANDING_BOUNDS = OUTSTANDING_RANGE_OPTIONS
    .map((o) => o.value)
    .filter((v) => v !== "" && v !== "no_dues")
    .map(Number);
const RECORD_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];
const PAGE_SIZE = 10;

const TYPE_OWN_FLEET = 1;
const TYPE_DCO = 2;
const TYPE_VDP = 3;

const DOC_FIELDS = [
    { key: "adhaar_url", label: "Aadhaar Card", icon: FiFileText },
    { key: "license_file_url", label: "Driving License", icon: FiFileText },
    { key: "pancard_file_url", label: "Pancard", icon: FiCreditCard },
    { key: "bank_passbook_photo_url", label: "Bank Passbook", icon: FiFileText },
    { key: "gas_bill_photo_url", label: "Gas Bill", icon: FiDroplet },
    { key: "electricity_bill_photo_url", label: "Electricity Bill", icon: FiZap },
    { key: "photo_url", label: "Driver Photo", icon: FiUser },
    { key: "payment_receipt_url", label: "Payment Receipt", icon: FiCreditCard },
    { key: "agreement_document_url", label: "Agreement Document", icon: FiFileText },
];

const isImageUrl = (url) => !!url && /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
const VIEW_KNOWN_KEYS = new Set([
    "id", "supplier_code", "supplier_name", "mobile", "alternate_mobile",
    "email", "aadhaar_number", "city", "state", "pincode",
    "branch_id", "service_location_id", "supplier_type_id", "status_id",
    "total_outstanding", "remarks", "created_at", "updated_at",
    "financial_year_id", "max_vehicle_capacity", "is_active",
    "created_by", "updated_by", "created_by_name", "updated_by_name",
    ...DOC_FIELDS.map((f) => f.key),
]);

const humanizeKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ACTION_MENU_MARGIN = 8;

export default function Suppliers() {
    const navigate = useNavigate();
    const currentUser = getCurrentUser() || {};
    const confirm = useConfirm();

    const isSuperadminUser = isSuperadmin();
    const canViewSuppliers =
        isSuperadminUser || hasPermission("suppliers", "view");
    const canEditSuppliers =
        isSuperadminUser || hasPermission("suppliers", "edit");
    const canDeleteSuppliers =
        isSuperadminUser || hasPermission("suppliers", "delete");

    const hasSupplierAccess =
        isSuperadminUser ||
        canViewSuppliers ||
        canEditSuppliers ||
        canDeleteSuppliers;
    const [suppliers, setSuppliers] = useState([]);
    const [supplierTypes, setSupplierTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const filterPanelRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSupplierType, setSelectedSupplierType] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedOutstandingRange, setSelectedOutstandingRange] = useState("");
    const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordStatus, setRecordStatus] = useState("active");

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLabel, setPreviewLabel] = useState("");

    const [docModalSupplier, setDocModalSupplier] = useState(null);

    const [viewSupplier, setViewSupplier] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState("");
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const printRef = useRef(null);

    const [openActionRowId, setOpenActionRowId] = useState(null);
    const [openActionIsInactive, setOpenActionIsInactive] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const actionMenuRef = useRef(null);
    const actionButtonRefs = useRef({});

    useEffect(() => {
        if (!hasSupplierAccess) {
            setLoading(false);
            return;
        }
        loadData();
    }, []);

    useEffect(() => {
        if (!hasSupplierAccess) return;
        loadSuppliers(selectedFinancialYear).catch(() => setErrorMsg("Couldn't load suppliers."));
    }, [selectedFinancialYear, recordStatus]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
                setFilterPanelOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const closeActionMenu = () => {
        setOpenActionRowId(null);
        setOpenActionIsInactive(false);
        setMenuPos(null);
    };

    useEffect(() => {
        if (openActionRowId === null) return;
        const handleClickOutside = (e) => {
            const btn = actionButtonRefs.current[openActionRowId];
            if (
                actionMenuRef.current && !actionMenuRef.current.contains(e.target) &&
                btn && !btn.contains(e.target)
            ) {
                closeActionMenu();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openActionRowId]);

    useEffect(() => {
        if (openActionRowId === null) return;
        const handleReposition = () => closeActionMenu();
        const wrap = document.querySelector(".sup-table-wrap");
        wrap?.addEventListener("scroll", handleReposition, { passive: true });
        window.addEventListener("resize", handleReposition);
        return () => {
            wrap?.removeEventListener("scroll", handleReposition);
            window.removeEventListener("resize", handleReposition);
        };
    }, [openActionRowId]);

    const toggleActionMenu = (e, supplierId, isInactiveRecord) => {
        e.stopPropagation();
        if (openActionRowId === supplierId) {
            closeActionMenu();
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const left = Math.min(
            rect.right,
            window.innerWidth - ACTION_MENU_MARGIN
        );
        const top = Math.min(
            rect.bottom + 6,
            window.innerHeight - ACTION_MENU_MARGIN
        );
        setMenuPos({ top, left });
        setOpenActionIsInactive(isInactiveRecord);
        setOpenActionRowId(supplierId);
    };

    const handleFinancialYearChange = (value) => {
        setSelectedFinancialYear(value);
        setDateFrom("");
        setDateTo("");
    };

    const selectedFYObj = useMemo(
        () => financialYears.find((fy) => String(fy.id) === String(selectedFinancialYear)),
        [financialYears, selectedFinancialYear]
    );
    const showFYDateRange = !!selectedFinancialYear && selectedFinancialYear !== "all";

    const loadData = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const [suppliersData, supplierTypesData, locationsData, branchesData, statusesData, financialYearsData] =
                await Promise.all([
                    getSuppliers({ status: recordStatus }), getSupplierTypes(), getServiceLocations(),
                    getBranches(), getStatuses(), getFinancialYears(),
                ]);
            setSuppliers(suppliersData || []);
            setSupplierTypes(supplierTypesData || []);
            setLocations(locationsData || []);
            setBranches(branchesData || []);
            setStatuses(statusesData || []);
            setFinancialYears(financialYearsData || []);
        } catch {
            setErrorMsg("Couldn't load suppliers. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const loadSuppliers = async (fyValue) => {
        const opts =
            fyValue === "all" ? { allYears: true, status: recordStatus }
                : fyValue ? { financialYearId: fyValue, status: recordStatus }
                    : { status: recordStatus };
        const data = await getSuppliers(opts);
        setSuppliers(data || []);
    };

    const getSupplierTypeName = (id) =>
        supplierTypes.find((t) => Number(t.id) === Number(id))?.supplier_type_name || "-";

    const getStatusName = (id) => {
        const s = statuses.find((s) => Number(s.id) === Number(id));
        return s?.name || s?.status_name || "-";
    };

    const getLocationName = (id) => {
        const l = locations.find((l) => Number(l.id) === Number(id));
        return l?.location_name || l?.name || "-";
    };

    const getBranchName = (id) => {
        const b = branches.find((b) => Number(b.id) === Number(id));
        return b?.branch_name || "-";
    };

    const getFinancialYearLabel = (id) =>
        financialYears.find((fy) => Number(fy.id) === Number(id))?.fy_label || "-";

    const hasOutstandingField = (s) =>
        Number(s.supplier_type_id) === TYPE_DCO || Number(s.supplier_type_id) === TYPE_VDP;

    const matchesOutstandingRange = (s) => {
        if (!selectedOutstandingRange) return true;

        const dues = Number(s.total_outstanding || 0);

        if (selectedOutstandingRange === "no_dues") {
            return !hasOutstandingField(s) || dues <= 0;
        }

        if (!hasOutstandingField(s)) return false;

        const lower = Number(selectedOutstandingRange);
        const idx = OUTSTANDING_BOUNDS.indexOf(lower);
        const upper = idx >= 0 && idx < OUTSTANDING_BOUNDS.length - 1
            ? OUTSTANDING_BOUNDS[idx + 1]
            : Infinity;

        return dues >= lower && dues < upper;
    };

    const matchesDateRange = (s) => {
        if (!dateFrom && !dateTo) return true;
        if (!s.created_at) return false;

        const created = new Date(s.created_at);
        if (dateFrom && created < new Date(dateFrom)) return false;
        if (dateTo) {
            const to = new Date(dateTo);
            to.setHours(23, 59, 59, 999);
            if (created > to) return false;
        }
        return true;
    };
    const resetFilters = () => {
        setSearchTerm(""); setSelectedSupplierType("");
        setSelectedStatus(""); setSelectedLocation(""); setSelectedBranch("");
        setSelectedOutstandingRange("");
        setSelectedFinancialYear("");
        setDateFrom(""); setDateTo("");
        setRecordStatus("active");
        setFilterPanelOpen(false);
    };

    const filteredSuppliers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return suppliers.filter((s) => {
            const matchesSearch =
                !term ||
                s.supplier_name?.toLowerCase().includes(term) ||
                s.supplier_id?.toLowerCase().includes(term) ||
                s.supplier_code?.toLowerCase().includes(term) ||
                s.mobile?.toLowerCase().includes(term) ||
                s.city?.toLowerCase().includes(term) ||
                s.remarks?.toLowerCase().includes(term);
            const matchesType = !selectedSupplierType || Number(s.supplier_type_id) === Number(selectedSupplierType);
            const matchesStatus = !selectedStatus || Number(s.status_id) === Number(selectedStatus);
            const matchesLocation = !selectedLocation || Number(s.service_location_id) === Number(selectedLocation);
            const matchesBranch = !selectedBranch || Number(s.branch_id) === Number(selectedBranch);
            const matchesFY = !selectedFinancialYear || Number(s.financial_year_id) === Number(selectedFinancialYear);
            return matchesSearch && matchesType && matchesStatus && matchesLocation && matchesBranch && matchesFY && matchesOutstandingRange(s) && matchesDateRange(s);
        });
    }, [suppliers, searchTerm, selectedSupplierType, selectedStatus, selectedLocation, selectedBranch, selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo]);

    useEffect(() => { setCurrentPage(1); },
        [searchTerm, selectedSupplierType, selectedStatus, selectedLocation, selectedBranch, selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo, recordStatus]);
    const totalPages = Math.max(1, Math.ceil(filteredSuppliers.length / PAGE_SIZE));
    const paginatedSuppliers = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredSuppliers.slice(start, start + PAGE_SIZE);
    }, [filteredSuppliers, currentPage]);

    useEffect(() => { closeActionMenu(); }, [currentPage, paginatedSuppliers]);

    const branchesForSelectedLocation = useMemo(() => {
        if (!selectedLocation) return branches;
        return branches.filter((b) => Number(b.location_id) === Number(selectedLocation));
    }, [branches, selectedLocation]);

    useEffect(() => {
        if (!selectedBranch) return;
        const stillValid = branchesForSelectedLocation.some(
            (b) => Number(b.id) === Number(selectedBranch)
        );
        if (!stillValid) setSelectedBranch("");
    }, [selectedLocation, branchesForSelectedLocation]);

    const rangeStart = filteredSuppliers.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredSuppliers.length);

    const goToPage = (page) => setCurrentPage(Math.min(Math.max(1, page), totalPages));
    const activeFilterCount = [
        selectedSupplierType, selectedStatus, selectedLocation, selectedBranch,
        selectedOutstandingRange, selectedFinancialYear, dateFrom, dateTo,
    ].filter(Boolean).length + (recordStatus !== "active" ? 1 : 0);

    const filtersActive = activeFilterCount > 0 || !!searchTerm;

    if (!hasSupplierAccess) {
        return (
            <div className="sup-shell">
                <Sidebar />
                <div className="sup-main">
                    <Header />
                    <div className="sup-page">
                        <div className="sup-error">
                            <FiAlertCircle size={16} />
                            <span>You don't have permission to view suppliers.</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const ownFleetCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_OWN_FLEET).length;
    const dcoCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_DCO).length;
    const vdpCount = filteredSuppliers.filter((s) => Number(s.supplier_type_id) === TYPE_VDP).length;

    const activeCount = filteredSuppliers.filter((s) => {
        const name = getStatusName(s.status_id).toLowerCase();
        return name.includes("active") && !name.includes("inactive");
    }).length;

    const inactiveCount = filteredSuppliers.filter((s) =>
        getStatusName(s.status_id).toLowerCase().includes("inactive") ||
        getStatusName(s.status_id).toLowerCase().includes("deactivate") ||
        getStatusName(s.status_id).toLowerCase().includes("block")
    ).length;

    const totalDues = filteredSuppliers
        .filter(hasOutstandingField)
        .reduce((sum, s) => sum + Number(s.total_outstanding || 0), 0);

    const handleDelete = async (id) => {
        if (!canDeleteSuppliers) return;

        const ok = await confirm({
            title: "Delete Supplier",
            message: "Are you sure you want to delete this supplier? This action cannot be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            variant: "danger",
        });
        if (!ok) return;

        setDeletingId(id);
        try {
            await deleteSupplier(id);
            await loadData();
        } catch {
            alert("Failed to delete supplier");
        } finally {
            setDeletingId(null);
        }
    };

    const statusClass = (name) => {
        const n = name.toLowerCase();
        if (n.includes("active") && !n.includes("in")) return "status-active";
        if (n.includes("inactive") || n.includes("deactivat")) return "status-inactive";
        if (n.includes("block")) return "status-inactive";
        if (n.includes("pending")) return "status-pending";
        return "status-default";
    };

    const openPreview = (url, label) => {
        if (!url) return;
        setPreviewUrl(url);
        setPreviewLabel(label);
    };

    const closePreview = () => {
        setPreviewUrl(null);
        setPreviewLabel("");
    };

    const openDocModal = (s) => setDocModalSupplier(s);
    const closeDocModal = () => setDocModalSupplier(null);

    const openViewModal = async (id, fallback) => {
        setViewSupplier(fallback || { id });
        setViewError("");
        setViewLoading(true);
        try {
            const data = await getSupplierById(id);
            setViewSupplier(data || fallback);
        } catch {
            setViewError("Couldn't load full supplier details. Showing available data.");
        } finally {
            setViewLoading(false);
        }
    };

    const closeViewModal = () => {
        setViewSupplier(null);
        setViewError("");
    };
    const handleDownloadView = async () => {
        if (!printRef.current || !viewSupplier || pdfGenerating) return;

        setPdfGenerating(true);

        const source = printRef.current;
        const originalOverflow = document.body.style.overflow;
        let host = null;

        try {
            const clone = source.cloneNode(true);

            clone.querySelectorAll(".no-print").forEach((el) => el.remove());

            clone.querySelectorAll("img").forEach((img) => {
                img.setAttribute("crossOrigin", "anonymous");
            });

            host = document.createElement("div");
            host.className = "sup-pdf-capture-host";
            host.style.cssText = [
                "position: fixed",
                "left: -100000px",
                "top: 0",
                "width: 210mm",
                "height: auto",
                "max-height: none",
                "overflow: visible",
                "background: #ffffff",
                "z-index: -1",
                "pointer-events: none",
            ].join(";");

            clone.classList.add("sup-pdf-capture-modal");
            clone.style.cssText = [
                "width: 210mm",
                "max-width: none",
                "height: auto",
                "min-height: 0",
                "max-height: none",
                "overflow: visible",
                "display: block",
                "position: static",
                "margin: 0",
                "padding: 0",
                "background: #ffffff",
                "box-shadow: none",
                "border: 0",
                "border-radius: 0",
            ].join(";");

            const body = clone.querySelector(".sup-view-body");
            if (body) {
                body.style.cssText += ";height:auto;min-height:0;max-height:none;overflow:visible;display:block;background:#ffffff;";
            }

            clone.querySelectorAll(
                ".sup-view-section, .sup-view-grid, .sup-view-doc-list, .sup-view-field, .sup-view-doc-row"
            ).forEach((el) => {
                el.style.breakInside = "avoid";
                el.style.pageBreakInside = "avoid";
                el.style.maxHeight = "none";
                el.style.overflow = "visible";
            });

            host.appendChild(clone);
            document.body.appendChild(host);
            document.body.style.overflow = "hidden";

            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const canvas = await html2canvas(clone, {
                scale: 2,
                backgroundColor: "#ffffff",
                useCORS: true,
                allowTaint: false,
                imageTimeout: 20000,
                ignoreElements: (el) => el.classList?.contains("no-print"),
                windowWidth: clone.scrollWidth,
                windowHeight: clone.scrollHeight,
                width: clone.scrollWidth,
                height: clone.scrollHeight,
                scrollX: 0,
                scrollY: 0,
            });

            const imgData = canvas.toDataURL("image/png", 1.0);
            const doc = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "a4",
                compress: true,
            });

            const margin = 0;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = margin;

            doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight, undefined, "FAST");
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                doc.addPage();
                doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight, undefined, "FAST");
                heightLeft -= pageHeight;
            }

            const safeName = (viewSupplier.supplier_code || viewSupplier.supplier_name || "supplier")
                .toString()
                .trim()
                .replace(/[^a-zA-Z0-9_-]+/g, "_");

            doc.save(`${safeName}.pdf`);
        } catch (err) {
            console.error("Failed to generate supplier PDF:", err);
            alert("Something went wrong generating the PDF. Please try again.");
        } finally {
            if (host && host.parentNode) host.parentNode.removeChild(host);
            document.body.style.overflow = originalOverflow;
            setPdfGenerating(false);
        }
    };

    const handlePrintView = () => {
        if (!printRef.current || !viewSupplier) return;

        const source = printRef.current;
        const printClone = source.cloneNode(true);
        const printHost = document.createElement("div");

        printHost.className = "sup-print-clone";
        printHost.style.cssText = [
            "position: static",
            "width: 210mm",
            "height: auto",
            "max-height: none",
            "overflow: visible",
            "margin: 0 auto",
            "padding: 0",
            "background: #ffffff",
        ].join(";");

        printClone.classList.add("sup-print-clone-modal");
        printClone.style.cssText = [
            "position: static",
            "width: 210mm",
            "max-width: none",
            "height: auto",
            "min-height: 0",
            "max-height: none",
            "overflow: visible",
            "display: block",
            "margin: 0 auto",
            "padding: 0",
            "background: #ffffff",
            "box-shadow: none",
            "border: 0",
            "border-radius: 0",
        ].join(";");

        printClone.querySelectorAll(".no-print").forEach((el) => el.remove());

        const body = printClone.querySelector(".sup-view-body");
        if (body) {
            body.style.cssText += ";height:auto;min-height:0;max-height:none;overflow:visible;display:block;background:#ffffff;";
        }

        printClone.querySelectorAll(
            ".sup-view-section, .sup-view-grid, .sup-view-doc-list, .sup-view-field, .sup-view-doc-row"
        ).forEach((el) => {
            el.style.breakInside = "avoid";
            el.style.pageBreakInside = "avoid";
            el.style.maxHeight = "none";
            el.style.overflow = "visible";
        });

        printHost.appendChild(printClone);
        document.body.appendChild(printHost);

        const cleanup = () => {
            if (printHost.parentNode) printHost.parentNode.removeChild(printHost);
            window.removeEventListener("afterprint", cleanup);
        };

        window.addEventListener("afterprint", cleanup);
        window.print();

    };

    const docCount = (s) => DOC_FIELDS.filter(({ key }) => !!s[key]).length;

    const openActionSupplier = openActionRowId !== null
        ? paginatedSuppliers.find((s) => s.id === openActionRowId)
        : null;

    return (
        <div className="sup-shell">
            <Sidebar />
            <div className="sup-main">
                <Header />
                <div className="sup-page">

                    <div className="sup-stats">
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{filteredSuppliers.length}</span>
                                <span className="sup-stat-lbl">
                                    {recordStatus === "inactive"
                                        ? "Inactive Suppliers"
                                        : filtersActive ? "Filtered Suppliers" : "Total Suppliers"}
                                </span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{ownFleetCount}</span>
                                <span className="sup-stat-lbl">Own Fleet</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{dcoCount}</span>
                                <span className="sup-stat-lbl">DCO</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{vdpCount}</span>
                                <span className="sup-stat-lbl">VDP</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{activeCount}</span>
                                <span className="sup-stat-lbl">Active</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">{inactiveCount}</span>
                                <span className="sup-stat-lbl">Inactive</span>
                            </div>
                        </div>
                        <div className="sup-stat">
                            <div>
                                <span className="sup-stat-val">₹{totalDues.toLocaleString("en-IN")}</span>
                                <span className="sup-stat-lbl">{filtersActive ? "Filtered Dues" : "Total Dues"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="sup-toolbar">
                        <div className="sup-toolbar-scroll">
                            <div className="sup-search">
                                <FiSearch size={14} />
                                <input
                                    type="text"
                                    placeholder="Search by name, ID, code, mobile, city…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className="sup-clear" onClick={() => setSearchTerm("")} aria-label="Clear">
                                        <FiX size={12} />
                                    </button>
                                )}
                            </div>

                            <div className="sup-filters">
                                <div className="sup-filter-dropdown" ref={filterPanelRef}>
                                    <button
                                        type="button"
                                        className="sup-filter-toggle-btn"
                                        onClick={() => setFilterPanelOpen((open) => !open)}
                                    >
                                        <FiFilter size={13} /> Filters
                                        {activeFilterCount > 0 && <span className="sup-badge">{activeFilterCount}</span>}
                                    </button>

                                    {filterPanelOpen && (
                                        <div className="sup-filter-panel">

                                            <div className="sup-filter-panel-field">
                                                <label>Status</label>
                                                <select value={recordStatus} onChange={(e) => setRecordStatus(e.target.value)}>
                                                    {RECORD_STATUS_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Type</label>
                                                <select value={selectedSupplierType} onChange={(e) => setSelectedSupplierType(e.target.value)}>
                                                    <option value="">All Types</option>
                                                    {supplierTypes.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.supplier_type_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Business Status</label>
                                                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                                    <option value="">All Business Statuses</option>
                                                    {statuses.map((s) => (
                                                        <option key={s.id} value={s.id}>{s.name || s.status_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Location</label>
                                                <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                                                    <option value="">All Locations</option>
                                                    {locations.map((l) => (
                                                        <option key={l.id} value={l.id}>{l.location_name || l.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Branch</label>
                                                <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                                                    <option value="">All Branches</option>
                                                    {branchesForSelectedLocation.map((b) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="sup-filter-panel-field">
                                                <label>Outstanding</label>
                                                <select value={selectedOutstandingRange} onChange={(e) => setSelectedOutstandingRange(e.target.value)}>
                                                    {OUTSTANDING_RANGE_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <button
                                                type="button"
                                                className="sup-filter-panel-done"
                                                onClick={() => setFilterPanelOpen(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <select value={selectedFinancialYear} onChange={(e) => handleFinancialYearChange(e.target.value)}>
                                    <option value="">Current Year</option>
                                    <option value="all">All Financial Years</option>
                                    {financialYears.map((fy) => (
                                        <option key={fy.id} value={fy.id}>{fy.fy_label}</option>
                                    ))}
                                </select>

                                {showFYDateRange && (
                                    <>
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            min={selectedFYObj?.start_date}
                                            max={selectedFYObj?.end_date}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            className="sup-date-input"
                                            title={`From date (within ${selectedFYObj?.fy_label || "selected year"})`}
                                        />
                                        <input
                                            type="date"
                                            value={dateTo}
                                            min={selectedFYObj?.start_date}
                                            max={selectedFYObj?.end_date}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className="sup-date-input"
                                            title={`To date (within ${selectedFYObj?.fy_label || "selected year"})`}
                                        />
                                    </>
                                )}

                                {filtersActive && (
                                    <button className="sup-reset-btn" onClick={resetFilters}>
                                        Reset {activeFilterCount > 0 && <span className="sup-badge">{activeFilterCount}</span>}
                                    </button>
                                )}
                            </div>
                        </div>

                        {hasSupplierAccess && (
                            <button className="sup-add-btn" onClick={() => navigate("/add-supplier")}>
                                <FiPlus size={15} /> Add Supplier
                            </button>
                        )}
                    </div>

                    {errorMsg && (
                        <div className="sup-error">
                            <FiAlertCircle size={16} />
                            <span>{errorMsg}</span>
                            <button onClick={loadData}><FiRefreshCw size={13} /> Retry</button>
                        </div>
                    )}

                    <div className="sup-table-card">
                        <div className="sup-table-wrap">
                            <table className="sup-table">
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th>Supplier Code</th>
                                        <th>Branch</th>
                                        <th>Type</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Status</th>
                                        <th>Outstanding</th>
                                        <th className="col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="9" className="sup-state">
                                                <div className="sup-loading">
                                                    <span className="sup-spinner" />
                                                    Loading suppliers…
                                                </div>
                                            </td>
                                        </tr>
                                    ) : paginatedSuppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="sup-state">
                                                <FiUsers size={28} style={{ opacity: 0.3 }} />
                                                <p>
                                                    {recordStatus === "inactive"
                                                        ? "No inactive (deleted) suppliers found."
                                                        : "No suppliers match your filters."}
                                                </p>
                                                <button className="sup-link-btn" onClick={resetFilters}>Clear filters</button>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedSuppliers.map((s) => {
                                            const statusName = getStatusName(s.status_id);
                                            const hasDues = hasOutstandingField(s);
                                            const dues = Number(s.total_outstanding || 0);
                                            const isInactiveRecord = s.is_active === false;
                                            return (
                                                <tr
                                                    key={s.id}
                                                    className={`${canViewSuppliers ? "sup-row-clickable" : ""} ${deletingId === s.id ? "row-deleting" : ""} ${isInactiveRecord ? "row-inactive" : ""}`}
                                                    onClick={() => {
                                                        if (canViewSuppliers) {
                                                            openViewModal(s.id, s);
                                                        }
                                                    }}
                                                    title={canViewSuppliers ? "Click to view full details" : ""}
                                                >
                                                    <td>
                                                        {s.photo_url && isImageUrl(s.photo_url) ? (
                                                            <img
                                                                src={resolveDocUrl(s.photo_url)}
                                                                alt={s.supplier_name}
                                                                className="sup-avatar-thumb"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openPreview(resolveDocUrl(s.photo_url), `${s.supplier_name} — Photo`);
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="sup-avatar-fallback">
                                                                <FiUser size={16} />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="cell-id">{s.supplier_code || "-"}</td>
                                                    <td>{getBranchName(s.branch_id)}</td>
                                                    <td>
                                                        <span className="sup-type-chip">
                                                            {getSupplierTypeName(s.supplier_type_id)}
                                                        </span>
                                                    </td>
                                                    <td className="cell-name">
                                                        {s.supplier_name}
                                                        {isInactiveRecord && (
                                                            <span className="sup-status status-inactive" style={{ marginLeft: 8 }}>
                                                                Deleted
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{s.mobile || "-"}</td>
                                                    <td>
                                                        <span className={`sup-status ${statusClass(statusName)}`}>
                                                            {statusName}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {hasDues ? (
                                                            <span className={dues > 0 ? "sup-dues has" : "sup-dues none"}>
                                                                ₹{dues.toLocaleString("en-IN")}
                                                            </span>
                                                        ) : "-"}
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div className="sup-actions-dropdown">
                                                            <button
                                                                type="button"
                                                                className={`sup-actions-btn ${openActionRowId === s.id ? "sup-actions-btn--active" : ""}`}
                                                                title="Actions"
                                                                ref={(el) => { actionButtonRefs.current[s.id] = el; }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onClick={(e) => toggleActionMenu(e, s.id, isInactiveRecord)}
                                                            >
                                                                Actions
                                                                <FiChevronDown size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && filteredSuppliers.length > 0 && (
                            <div className="sup-pagination">
                                <span className="sup-pagination-info">
                                    Showing {rangeStart}–{rangeEnd} of {filteredSuppliers.length} suppliers
                                </span>
                                <div className="sup-pagination-controls">
                                    <button className="sup-page-btn" onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1} aria-label="Previous page">
                                        <FiChevronLeft size={14} />
                                    </button>
                                    <span className="sup-page-current">Page {currentPage} of {totalPages}</span>
                                    <button className="sup-page-btn" onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages} aria-label="Next page">
                                        <FiChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {openActionRowId !== null && menuPos && createPortal(
                <div
                    className="sup-actions-menu sup-actions-menu-portal"
                    ref={actionMenuRef}
                    style={{
                        top: menuPos.top,
                        left: menuPos.left,
                        transform: "translateX(-100%)",
                    }}
                >
                    <>
                        {canViewSuppliers && (
                            <button
                                className="sup-actions-menu-item"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    openViewModal(id, openActionSupplier);
                                }}
                            >
                                <FiEye size={13} /> View
                            </button>
                        )}

                        {canEditSuppliers && (
                            <button
                                className="sup-actions-menu-item"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    navigate(`/edit-supplier/${id}`);
                                }}
                            >
                                <FiEdit2 size={13} /> Edit
                            </button>
                        )}

                        {canDeleteSuppliers && !openActionIsInactive && (
                            <button
                                className="sup-actions-menu-item delete"
                                onClick={() => {
                                    const id = openActionRowId;
                                    closeActionMenu();
                                    handleDelete(id);
                                }}
                                disabled={deletingId === openActionRowId}
                            >
                                <FiTrash2 size={13} /> Delete
                            </button>
                        )}

                        {!canViewSuppliers && !canEditSuppliers && !canDeleteSuppliers && (
                            <div style={{ padding: "8px 12px", fontSize: 12, color: "#64748b" }}>
                                View only
                            </div>
                        )}
                    </>
                </div>,
                document.body
            )}

            {docModalSupplier && (
                <div className="sup-docmodal-overlay" onClick={closeDocModal}>
                    <div className="sup-docmodal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-docmodal-header">
                            <div>
                                <h3>Documents</h3>
                                <p>
                                    — {docModalSupplier.supplier_name}
                                    {docModalSupplier.mobile && <> &nbsp;{docModalSupplier.mobile}</>}
                                </p>
                            </div>
                            <button className="sup-preview-close" onClick={closeDocModal} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="sup-docmodal-grid">
                            {DOC_FIELDS.map(({ key, label, icon: Icon }) => {
                                const rawUrl = docModalSupplier[key];
                                const url = resolveDocUrl(rawUrl);
                                return (
                                    <div className="sup-doc-card" key={key}>
                                        <span className="sup-doc-card-label">{label}</span>
                                        {url ? (
                                            isImageUrl(url) ? (
                                                <img
                                                    src={url}
                                                    alt={label}
                                                    className="sup-doc-card-img"
                                                    onClick={() => openPreview(url, `${docModalSupplier.supplier_name} — ${label}`)}
                                                />
                                            ) : (
                                                <div
                                                    className="sup-doc-card-file"
                                                    onClick={() => openPreview(url, `${docModalSupplier.supplier_name} — ${label}`)}
                                                >
                                                    <Icon size={28} />
                                                </div>
                                            )
                                        ) : (
                                            <div className="sup-doc-card-empty">
                                                <Icon size={24} />
                                                <span>Not uploaded</span>
                                            </div>
                                        )}
                                        {url && (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="sup-doc-card-view"
                                            >
                                                <FiEye size={13} /> View full
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="sup-docmodal-footer">
                            {docCount(docModalSupplier)} of {DOC_FIELDS.length} documents uploaded
                        </div>
                    </div>
                </div>
            )}
            {previewUrl && (
                <div className="sup-preview-overlay" onClick={closePreview}>
                    <div className="sup-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-preview-header">
                            <span>{previewLabel}</span>
                            <button className="sup-preview-close" onClick={closePreview} aria-label="Close">
                                <FiX size={16} />
                            </button>
                        </div>
                        <div className="sup-preview-body">
                            {isImageUrl(previewUrl) ? (
                                <img src={previewUrl} alt={previewLabel} className="sup-preview-image" />
                            ) : (
                                <iframe src={previewUrl} title={previewLabel} className="sup-preview-pdf" />
                            )}
                        </div>
                        <div className="sup-preview-footer">
                            <a href={previewUrl} target="_blank" rel="noreferrer" className="sup-preview-link">
                                <FiEye size={13} /> Open in new tab
                            </a>
                            <a href={previewUrl} download className="sup-preview-link">
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {viewSupplier && (
                <div className="sup-view-overlay" onClick={closeViewModal}>
                    <div ref={printRef} className="sup-view-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sup-view-header">
                            <div className="sup-view-header-left">
                                {viewSupplier.photo_url && isImageUrl(viewSupplier.photo_url) ? (
                                    <img src={resolveDocUrl(viewSupplier.photo_url)} alt={viewSupplier.supplier_name} className="sup-view-avatar" />
                                ) : (
                                    <div className="sup-view-avatar sup-view-avatar-fallback">
                                        <FiUser size={22} />
                                    </div>
                                )}
                                <div>
                                    <h3>
                                        {viewSupplier.supplier_name || "Supplier"}
                                        {viewSupplier.is_active === false && (
                                            <span className="sup-status status-inactive" style={{ marginLeft: 8 }}>
                                                Deleted
                                            </span>
                                        )}
                                    </h3>
                                    <p>{viewSupplier.supplier_code || "-"} {viewSupplier.mobile && <>&nbsp;·&nbsp;{viewSupplier.mobile}</>}</p>
                                </div>
                            </div>
                            <div className="sup-view-header-actions no-print">
                                <button className="sup-view-print-btn" onClick={handlePrintView}>
                                    <FiPrinter size={14} /> Print
                                </button>
                                <button className="sup-view-download-btn" onClick={handleDownloadView} disabled={pdfGenerating}>
                                    <FiDownload size={14} /> {pdfGenerating ? "Preparing…" : "Download"}
                                </button>
                                <button className="sup-preview-close" onClick={closeViewModal} aria-label="Close">
                                    <FiX size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="sup-view-body">
                            {viewLoading && (
                                <div className="sup-view-loading">
                                    <span className="sup-spinner" />
                                    Loading full details…
                                </div>
                            )}

                            {viewError && (
                                <div className="sup-error" style={{ marginBottom: 16 }}>
                                    <FiAlertCircle size={16} />
                                    <span>{viewError}</span>
                                </div>
                            )}

                            <div className="sup-view-section">
                                <h4>Contact</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>Supplier Code</span><strong>{viewSupplier.supplier_code || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Name</span><strong>{viewSupplier.supplier_name || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Mobile</span><strong>{viewSupplier.mobile || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Alt. Mobile</span><strong>{viewSupplier.alternate_mobile || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Email</span><strong>{viewSupplier.email || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Aadhaar</span><strong>{viewSupplier.aadhaar_number || "-"}</strong></div>
                                </div>
                            </div>

                            <div className="sup-view-section">
                                <h4>Location</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>City</span><strong>{viewSupplier.city || "-"}</strong></div>
                                    <div className="sup-view-field"><span>State</span><strong>{viewSupplier.state || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Pincode</span><strong>{viewSupplier.pincode || "-"}</strong></div>
                                    <div className="sup-view-field"><span>Branch</span><strong>{getBranchName(viewSupplier.branch_id)}</strong></div>
                                    <div className="sup-view-field"><span>Service Location</span><strong>{getLocationName(viewSupplier.service_location_id)}</strong></div>
                                </div>
                            </div>

                            <div className="sup-view-section">
                                <h4>Status</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field"><span>Type</span><strong>{getSupplierTypeName(viewSupplier.supplier_type_id)}</strong></div>
                                    <div className="sup-view-field"><span>Status</span><strong>{getStatusName(viewSupplier.status_id)}</strong></div>
                                    <div className="sup-view-field"><span>Financial Year</span><strong>{getFinancialYearLabel(viewSupplier.financial_year_id)}</strong></div>
                                    <div className="sup-view-field">
                                        <span>Outstanding</span>
                                        <strong>
                                            {hasOutstandingField(viewSupplier)
                                                ? `₹${Number(viewSupplier.total_outstanding || 0).toLocaleString("en-IN")}`
                                                : "-"}
                                        </strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Max Vehicle Capacity</span>
                                        <strong>
                                            {hasOutstandingField(viewSupplier)
                                                ? (viewSupplier.max_vehicle_capacity ?? "-")
                                                : "-"}
                                        </strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Record Status</span>
                                        <strong>{viewSupplier.is_active === false ? "Inactive (Deleted)" : "Active"}</strong>
                                    </div>
                                    <div className="sup-view-field sup-view-field-wide"><span>Remarks</span><strong>{viewSupplier.remarks || "-"}</strong></div>
                                </div>
                            </div>
                            <div className="sup-view-section">
                                <h4>Audit</h4>
                                <div className="sup-view-grid">
                                    <div className="sup-view-field">
                                        <span>Created By</span>
                                        <strong>{viewSupplier.created_by_name || viewSupplier.created_by || "-"}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Updated By</span>
                                        <strong>{viewSupplier.updated_by_name || viewSupplier.updated_by || "-"}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Created On</span>
                                        <strong>{formatServerDateTime(viewSupplier.created_at)}</strong>
                                    </div>
                                    <div className="sup-view-field">
                                        <span>Updated On</span>
                                        <strong>{formatServerDateTime(viewSupplier.updated_at)}</strong>
                                    </div>
                                </div>
                            </div>
                            <div className="sup-view-section">
                                <h4>Documents</h4>
                                <div className="sup-view-doc-list">
                                    {DOC_FIELDS.map(({ key, label, icon: Icon }) => {
                                        const rawUrl = viewSupplier[key];
                                        const url = resolveDocUrl(rawUrl);
                                        const showThumb = url && isImageUrl(url);
                                        return (
                                            <div className="sup-view-doc-row" key={key}>
                                                {showThumb ? (
                                                    <img
                                                        src={url}
                                                        alt={label}
                                                        className="sup-view-doc-thumb"
                                                        onClick={() => openPreview(url, `${viewSupplier.supplier_name} — ${label}`)}
                                                    />
                                                ) : (
                                                    <div className="sup-view-doc-thumb sup-view-doc-thumb-icon">
                                                        <Icon size={14} />
                                                    </div>
                                                )}
                                                <span>{label}</span>
                                                {url ? (
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => {
                                                            if (showThumb) {
                                                                e.stopPropagation();
                                                            }
                                                        }}
                                                    >
                                                        View / Download
                                                    </a>
                                                ) : (
                                                    <em>Not uploaded</em>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {(() => {
                                const extraEntries = Object.entries(viewSupplier).filter(
                                    ([k, v]) => !VIEW_KNOWN_KEYS.has(k) && v !== null && v !== undefined && v !== ""
                                );
                                if (extraEntries.length === 0) return null;
                                return (
                                    <div className="sup-view-section">
                                        <h4>Additional Details</h4>
                                        <div className="sup-view-grid">
                                            {extraEntries.map(([k, v]) => (
                                                <div className="sup-view-field" key={k}>
                                                    <span>{humanizeKey(k)}</span>
                                                    <strong>{typeof v === "object" ? JSON.stringify(v) : String(v)}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}